import type { SystemStatus } from "@/lib/types";
import { splitWorkers } from "@/components/WorkerBadges";

export type Level = "green" | "yellow" | "red";

/** The apps that must send heartbeats (internal/app StartWorker: hub-worker). */
export const EXPECTED_WORKERS = ["hub-worker"];
/** A worker silent this long is late (yellow); this long, down (red). */
const LATE_MS = 2 * 60_000;
const DOWN_MS = 10 * 60_000;

export const LEVEL_WORD: Record<Level, string> = { green: "All systems normal", yellow: "Something to check", red: "Down" };

/**
 * One green/yellow/red answer for the home page (Brandon, 2026-11-04). Red: database or Valkey down, or no worker.
 * Yellow: outage writes waiting or set aside, a worker late, or email sending paused. Green: everything else.
 */
export function systemLevel(
  s: SystemStatus & { spool_dead_unresolved?: number },
  opts: { sendingPaused?: boolean; now?: number } = {},
): { level: Level; word: string; reasons: string[] } {
  const now = opts.now ?? Date.now();
  const red: string[] = [];
  const yellow: string[] = [];
  if (s.database !== "up") red.push("Database is down");
  if (s.valkey !== "up") red.push("Valkey (the fast cache) is down");
  const { current } = splitWorkers(s.workers ?? []);
  for (const app of EXPECTED_WORKERS) {
    const w = current.find((x) => x.app === app);
    const age = w ? now - new Date(w.last_seen_at).getTime() : Infinity;
    if (age >= DOWN_MS) red.push(w ? `The background worker hasn't checked in for ${Math.round(age / 60_000)} minutes` : "No background worker is running");
    else if (age >= LATE_MS) yellow.push(`The background worker is late (last seen ${Math.round(age / 60_000)} minutes ago)`);
  }
  const waiting = (s.spool?.valkey_pending ?? 0) + (s.spool?.spaces_pending ?? 0);
  if (waiting > 0) yellow.push(`${waiting} saved changes are waiting to be written`);
  if ((s.spool_dead_unresolved ?? 0) > 0) yellow.push(`${s.spool_dead_unresolved} saved changes were set aside during an outage`);
  if (opts.sendingPaused) yellow.push("Email sending is paused");
  const level: Level = red.length ? "red" : yellow.length ? "yellow" : "green";
  return { level, word: LEVEL_WORD[level], reasons: [...red, ...yellow] };
}
